import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/**
 * PatientPayLanding — Public permanent landing page for patient-level payments.
 * Accessed via /pay/:practiceGuid/:patientGuid (no authentication required).
 *
 * Used by CollectionEngine reminders as the patient's always-current "pay my balance" URL.
 * Queries athena for balance at load time (no stale data).
 */
export default function PatientPayLanding() {
  const { practiceGuid, patientGuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  // Stripe state
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [stripeCardEl, setStripeCardEl] = useState(null);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/pay/${practiceGuid}/${patientGuid}`);
      if (res.success) {
        setPageData(res.data);
        setAmount(String(res.data.amountDue || ''));
      } else {
        setError(res.error || 'Unable to load payment page');
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [practiceGuid, patientGuid]);

  useEffect(() => { loadPage(); }, [loadPage]);

  // Load Stripe.js when needed
  useEffect(() => {
    if (!pageData || pageData.gateway !== 'STRIPE') return;
    if (window.Stripe) { setStripeLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => setStripeLoaded(true);
    document.head.appendChild(script);
  }, [pageData]);

  // Mount Stripe Elements once Stripe.js + page data ready
  useEffect(() => {
    if (!stripeLoaded || !pageData?.publishableKey || pageData.gateway !== 'STRIPE') return;
    if (stripeInstance) return;
    const stripe = window.Stripe(pageData.publishableKey);
    const elements = stripe.elements();
    const card = elements.create('card', {
      style: {
        base: { fontSize: '16px', color: '#374151', fontFamily: 'system-ui, sans-serif',
                '::placeholder': { color: '#9ca3af' } },
        invalid: { color: '#dc2626' },
      },
    });
    const mountEl = document.getElementById('pay-card-element');
    if (mountEl) {
      card.mount(mountEl);
      setStripeInstance(stripe);
      setStripeCardEl(card);
    }
  }, [stripeLoaded, pageData, stripeInstance]);

  // ─── Stripe pay handler ───────────────────────────
  const handleStripePay = async () => {
    if (!stripeInstance || !stripeCardEl) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setStatus('Enter a valid amount'); return; }
    if (amt > pageData.amountDue) { setStatus(`Amount cannot exceed balance of $${pageData.amountDue}`); return; }

    setProcessing(true);
    setStatus('Processing payment...');

    try {
      // 1. Create PaymentIntent via backend
      const intentRes = await api.post(`/pay/${practiceGuid}/${patientGuid}/intent`, { amount: amt });
      if (!intentRes.success) throw new Error(intentRes.error || 'Failed to create payment intent');

      const { clientSecret, linkToken } = intentRes.data;

      // 2. Confirm with Stripe
      const confirmed = await stripeInstance.confirmCardPayment(clientSecret, {
        payment_method: { card: stripeCardEl },
      });

      if (confirmed.error) {
        setStatus('Card error: ' + confirmed.error.message);
        setProcessing(false);
        return;
      }

      // 3. Notify backend
      await api.post('/payments/confirm', { linkToken, paymentIntentId: confirmed.paymentIntent.id });

      setSuccess({ amount: amt, last4: confirmed.paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.last4 });
      setStatus('');
    } catch (err) {
      setStatus('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ─── Elavon pay handler ───────────────────────────
  const handleElavonPay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setStatus('Enter a valid amount'); return; }
    if (amt > pageData.amountDue) { setStatus(`Amount cannot exceed balance of $${pageData.amountDue}`); return; }

    setProcessing(true);
    setStatus('Creating Elavon payment session...');

    try {
      const intentRes = await api.post(`/pay/${practiceGuid}/${patientGuid}/intent`, { amount: amt });
      if (!intentRes.success) throw new Error(intentRes.error || 'Failed to create session');

      const { sessionId, isSandbox } = intentRes.data;
      const scriptUrl = isSandbox
        ? 'https://uat.hpp.converge.eu.elavonaws.com/client/library.js'
        : 'https://hpp.na.elavonpayments.com/client/library.js';

      const openLightbox = () => {
        if (!window.ElavonLightbox) { setStatus('Error: Elavon SDK not loaded'); setProcessing(false); return; }
        const lightbox = new window.ElavonLightbox({
          sessionId,
          onReady: (err) => {
            if (err) { setStatus('Lightbox error: ' + err.message); setProcessing(false); return; }
            lightbox.show();
          },
          messageHandler: (message, defaultAction) => {
            if (message.type === 'transactionCreated' && message.isAuthorized) {
              setSuccess({ amount: amt, transactionId: message.transaction });
              setStatus('');
              setProcessing(false);
            } else if (message.type === 'transactionCreated') {
              setStatus('Payment declined. Please try a different card.');
              setProcessing(false);
            } else if (message.type === 'error') {
              setStatus('Error: ' + (message.error || 'unknown'));
              setProcessing(false);
            }
            defaultAction();
          },
        });
      };

      if (window.ElavonLightbox) {
        openLightbox();
      } else {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = openLightbox;
        script.onerror = () => { setStatus('Failed to load Elavon SDK'); setProcessing(false); };
        document.head.appendChild(script);
      }
    } catch (err) {
      setStatus('Error: ' + err.message);
      setProcessing(false);
    }
  };

  // ─── Render ────────────────────────────────────────
  const wrap = { maxWidth: 520, margin: '0 auto', padding: '2rem 1rem', fontFamily: 'system-ui, -apple-system, sans-serif' };

  if (loading) {
    return <div style={{ ...wrap, textAlign: 'center', color: '#6b7280' }}>Loading your balance...</div>;
  }

  if (error) {
    return (
      <div style={wrap}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '1rem', borderRadius: 8 }}>
          {error === 'Practice not found' || error === 'Patient not found'
            ? 'This payment link is not valid. Please contact your practice.'
            : error}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={wrap}>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✓</div>
          <h2 style={{ fontSize: '1.5rem', color: '#166534', marginBottom: '0.5rem' }}>Payment received</h2>
          <p style={{ color: '#166534', marginBottom: '0.25rem' }}>Thank you, {pageData.patientFirstName}!</p>
          <p style={{ color: '#374151', fontSize: '1.25rem', fontWeight: 600 }}>${success.amount.toFixed(2)}</p>
          {success.last4 && <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Card ending in {success.last4}</p>}
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '1rem' }}>A receipt has been sent to you.</p>
        </div>
      </div>
    );
  }

  const hasBalance = pageData.amountDue > 0;

  return (
    <div style={wrap}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', color: '#111827', marginBottom: '0.25rem' }}>{pageData.practiceName}</h1>
        <p style={{ color: '#6b7280' }}>Hello {pageData.patientFirstName}</p>
      </div>

      {/* Balance card */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {hasBalance ? (
          <>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Balance due</p>
            <p style={{ fontSize: '2.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
              ${Number(pageData.amountDue).toFixed(2)}
            </p>

            {pageData.allowPartialPayment && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                  Amount to pay
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem', color: '#6b7280' }}>$</span>
                  <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                    style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem', flex: 1 }}
                    disabled={processing} />
                </div>
                {pageData.minimumPayment && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Minimum: ${pageData.minimumPayment}
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p style={{ textAlign: 'center', color: '#059669', padding: '1rem', fontWeight: 500 }}>
            No balance due. Thank you!
          </p>
        )}
      </div>

      {/* Payment form */}
      {hasBalance && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.5rem', marginBottom: '1rem' }}>
          {pageData.gateway === 'STRIPE' && (
            <>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                Card details
              </label>
              <div id="pay-card-element" style={{
                padding: '12px', border: '1px solid #d1d5db', borderRadius: 8, marginBottom: '1rem', background: '#fff',
                minHeight: 44,
              }} />
              <button onClick={handleStripePay} disabled={processing || !stripeInstance}
                style={payButton(processing)}>
                {processing ? 'Processing...' : `Pay $${parseFloat(amount || 0).toFixed(2)}`}
              </button>
            </>
          )}

          {pageData.gateway === 'ELAVON' && (
            <>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Click below to securely enter your card details.
              </p>
              <button onClick={handleElavonPay} disabled={processing}
                style={payButton(processing)}>
                {processing ? 'Opening...' : `Pay $${parseFloat(amount || 0).toFixed(2)}`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Status */}
      {status && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, fontSize: '0.875rem',
          background: status.startsWith('Error') || status.includes('error') || status.includes('decline') ? '#fef2f2' : '#eff6ff',
          color: status.startsWith('Error') || status.includes('error') || status.includes('decline') ? '#991b1b' : '#1e40af',
          border: `1px solid ${status.startsWith('Error') || status.includes('error') || status.includes('decline') ? '#fecaca' : '#bfdbfe'}`,
          marginBottom: '1rem',
        }}>
          {status}
        </div>
      )}

      {/* PCI footer */}
      <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9ca3af', marginTop: '2rem' }}>
        Your card details are processed securely by {pageData.gateway === 'STRIPE' ? 'Stripe' : 'Elavon'}.<br />
        Card information is never stored on {pageData.practiceName} servers.
      </p>
    </div>
  );
}

function payButton(processing) {
  return {
    width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
    background: processing ? '#9ca3af' : '#2563eb', color: '#fff', fontSize: '1rem', fontWeight: 600,
    cursor: processing ? 'not-allowed' : 'pointer',
  };
}
